import React, { useEffect, useState } from 'react';
import { Label } from 'react-bootstrap';
import { connect } from 'react-redux';
import { getServiceInvokerRequestsByState, getServiceInvokerRequest } from '../../sources/serviceInvoker';
import Modal from '../generic/Modal';
import SyntaxHighlighter from '../generic/SyntaxHighlighter';
import { addNotification } from '../../actions/reduxActions/notification';
import { notificationTypes } from '../../constants';
import moment from 'moment';
import Pager from '../Pager';
import Table from '../Table';
import { formatJSONContent } from '../../utils/core';
import RootFaultsList from './RootFaultsList';

function renderStatus(attributes) {
    if (attributes.numberOfFailures > 0) {
        return <Label bsStyle="danger">Failed</Label>;
    }

    return <Label bsStyle="success">OK</Label>;
}

export const ServiceInvokerRequestList = ({ addNotification, currentUser, selectedState }) => {
    const [page, setPage] = useState(1);
    const pageSize = 50;
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [requests, setRequests] = useState([]);
    const [content, setContent] = useState(null);
    const [showContent, setShowContent] = useState(false);

    function refreshRequests(nextPage) {
        const params = {
            authenticationToken: currentUser.authenticationToken,
            tenantId: currentUser.tenantId,
            stateId: selectedState.id,
            page: nextPage,
            pageSize,
        };

        setPage(nextPage);
        setLoading(true);

        getServiceInvokerRequestsByState(params)
            .then((response) => {
                setRequests(response.items);
                // eslint-disable-next-line no-underscore-dangle
                setTotal(response._meta.total);
            })
            .catch(error => addNotification({
                type: notificationTypes.ERROR,
                message: error.message,
                isPersistent: true,
            }))
            .finally(() => setLoading(false));
    }

    useEffect(() => refreshRequests(1), [selectedState]);

    function onShowFailures({ id: requestId }) { 
        const params = {
            authenticationToken: currentUser.authenticationToken,
            tenantId: currentUser.tenantId,
            requestId,
        };

        getServiceInvokerRequest(params)
            .then(({ failures }) => {
                setContent(failures ? JSON.stringify(failures) : '');
                setShowContent(true);
            })
            .catch(error => addNotification({
                type: notificationTypes.ERROR,
                message: error.message,
                isPersistent: true,
            }))
            .finally(() => setLoading(false));
    }

    const columns = [
        {
            renderHeader: () => 'Occurred At (UTC)',
            renderCell: ({ item }) => moment(item.createdAt).utc().format('MMM Do YYYY, HH:mm:ss'),
            size: '200px',
        },
        {
            renderHeader: () => 'URI',
            renderCell: ({ item }) => item.uri,
        },
        {
            renderHeader: () => 'Method',
            renderCell: ({ item }) => item.method,
            size: '100px',
        },
        {
            renderHeader: () => 'Status',
            renderCell: ({ item }) => renderStatus(item.attributes),
            size: '100px',
        },
        {
            renderHeader: () => 'Actions',
            renderCell: ({ item }) => (
                <>
                    <button
                        className="link-emulate"
                        onClick={() => onShowFailures(item)}
                    >
                        Failures
                    </button>
                </>
            ),
            size: '200px',
        },
    ];

    return (
        <div>
            <h1>Service Log Events</h1>

            <Table
                className="table"
                columns={columns}
                items={requests}
                isLoading={loading}
            />

            <Pager
                page={page}
                pageSize={pageSize}
                total={total}
                onPageClick={nextPage => refreshRequests(nextPage)}
            />

            <h1>Root Faults</h1>

            <RootFaultsList selectedState={selectedState} />

            <Modal
                show={showContent}
                onHide={() => setShowContent(false)}
                title="Failures"
                renderBody={() => (
                    <SyntaxHighlighter
                        content={formatJSONContent(content)}
                        language="json"
                    />
                )}
                renderFooter={() => (
                    <div className="flex">
                        <div className="flex-child-right">
                            <button className="btn btn-sm btn-default" onClick={() => setShowContent(false)}>
                                Close
                            </button>
                        </div>
                    </div>
                )}
            />
        </div>
    );
};

const mapStateToProps = state => ({
    currentUser: state.currentUser,
});

const mapDispatchToProps = ({
    addNotification,
});

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(ServiceInvokerRequestList);
